import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Card, Table, Form, Button, Spinner, Alert,
} from 'react-bootstrap';
import Layout from '../components/Layout';
import qnaService from '../services/qnaService';

const CATEGORIES_READ_URL = process.env.REACT_APP_N8N_QNA_CATEGORIES_READ_URL;
const SECRET = process.env.REACT_APP_N8N_SECRET;

const breadcrumbs = [
  { label: 'الرئيسية', path: '/dashboard', icon: 'bi-house-door' },
  { label: 'الأسئلة والأجوبة', path: '/qna', icon: 'bi-chat-dots' },
  { label: 'التصنيفات', icon: 'bi-bookmarks' },
];

const fetchCategories = async () => {
  if (!CATEGORIES_READ_URL) {
    throw new Error('REACT_APP_N8N_QNA_CATEGORIES_READ_URL غير مضبوط في ملف .env');
  }
  const res = await fetch(CATEGORIES_READ_URL, {
    headers: { 'x-api-key': SECRET || '' },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const text = await res.text();
  if (!text || !text.trim()) throw new Error('الخادم أعاد استجابة فارغة');
  const json = JSON.parse(text);
  if (json.error) throw new Error(json.error);
  return (json.categories || []).map((c) => ({
    value: c.value ?? c.label ?? c,
    label: c.label ?? c.value ?? c,
  }));
};

const AddCategoryRow = ({ onAdded }) => {
  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const handleAdd = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setAdding(true);
    setAddError('');
    try {
      await qnaService.createCategory(trimmed);
      setName('');
      onAdded();
    } catch (e) {
      setAddError(e.message || 'فشل الإضافة');
    } finally {
      setAdding(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm mb-4">
      <Card.Body>
        <div className="fw-semibold mb-3" style={{ color: 'var(--green-dark)' }}>
          <i className="bi bi-plus-circle ms-2" />
          إضافة تصنيف جديد
        </div>
        {addError && (
          <Alert variant="danger" className="py-2 small">{addError}</Alert>
        )}
        <div className="d-flex gap-2">
          <Form.Control
            placeholder="اسم التصنيف..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            disabled={adding}
          />
          <Button
            style={{ background: 'var(--green-dark)', borderColor: 'var(--green-dark)' }}
            disabled={adding || !name.trim()}
            onClick={handleAdd}
          >
            {adding ? <Spinner size="sm" /> : 'إضافة'}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

const CategoryRow = ({ category, index, onReload }) => {
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState(category.label);
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState('');

  const handleSave = async () => {
    const trimmed = editName.trim();
    if (!trimmed) return;
    setSaving(true);
    setRowError('');
    try {
      await qnaService.updateCategoryName(category.value, trimmed);
      setEditMode(false);
      onReload();
    } catch (e) {
      setRowError(e.message || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التصنيف؟')) return;
    setSaving(true);
    setRowError('');
    try {
      await qnaService.deleteCategory(category.value);
      onReload();
    } catch (e) {
      setRowError(e.message || 'فشل الحذف');
      setSaving(false);
    }
  };

  return (
    <tr>
      <td className="text-muted" style={{ width: 55 }}>{index + 1}</td>
      <td>
        {editMode ? (
          <Form.Control
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            disabled={saving}
            autoFocus
          />
        ) : (
          <span>{category.label}</span>
        )}
        {rowError && (
          <div className="text-danger small mt-1">{rowError}</div>
        )}
      </td>
      <td style={{ width: 140 }}>
        {editMode ? (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              style={{ background: 'var(--green-dark)', borderColor: 'var(--green-dark)' }}
              disabled={saving || !editName.trim()}
              onClick={handleSave}
            >
              {saving ? <Spinner size="sm" /> : <i className="bi bi-check-lg" />}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={saving}
              onClick={() => { setEditMode(false); setEditName(category.label); setRowError(''); }}
            >
              <i className="bi bi-x-lg" />
            </Button>
          </div>
        ) : (
          <div className="d-flex gap-1">
            <Button
              size="sm"
              variant="outline-secondary"
              title="تعديل"
              disabled={saving}
              onClick={() => { setEditMode(true); setEditName(category.label); }}
            >
              <i className="bi bi-pencil" />
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              title="حذف"
              disabled={saving}
              onClick={handleDelete}
            >
              {saving ? <Spinner size="sm" /> : <i className="bi bi-trash" />}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default function QNACategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchCategories();
      setCategories(data);
    } catch (e) {
      setError('تعذّر تحميل التصنيفات: ' + (e.message || 'خطأ غير معروف'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout title="تصنيفات الأسئلة والأجوبة" breadcrumbs={breadcrumbs}>
      <Container fluid dir="rtl">

        {/* Page header */}
        <div className="d-flex align-items-center gap-3 mb-4">
          <h5 className="mb-0 fw-bold" style={{ color: 'var(--green-dark)' }}>
            <i className="bi bi-bookmarks ms-2" />
            تصنيفات الأسئلة والأجوبة
          </h5>
          {!loading && (
            <span
              className="badge"
              style={{
                background: 'rgba(27,77,42,0.1)',
                color: 'var(--green-dark)',
                border: '1px solid rgba(27,77,42,0.2)',
                fontSize: '0.85rem',
                padding: '4px 12px',
              }}
            >
              {categories.length}
            </span>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            className="me-auto"
            onClick={load}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise ms-1" />
            تحديث
          </Button>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <i className="bi bi-exclamation-triangle-fill" />
            {error}
          </Alert>
        )}

        {/* Add new */}
        <AddCategoryRow onAdded={load} />

        {/* Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: 'var(--green-dark)' }} />
            <div className="text-muted mt-3">جارٍ تحميل التصنيفات...</div>
          </div>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle" style={{ fontSize: '0.92rem' }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 55 }}>#</th>
                      <th>الاسم</th>
                      <th style={{ width: 140 }}>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center py-5 text-muted">
                          <i className="bi bi-bookmarks fs-3 d-block mb-2" />
                          لا توجد تصنيفات بعد
                        </td>
                      </tr>
                    ) : (
                      categories.map((cat, idx) => (
                        <CategoryRow
                          key={cat.value}
                          category={cat}
                          index={idx}
                          onReload={load}
                        />
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
    </Layout>
  );
}
